import { readFile, saveFile, Watcher } from "filebridge-client";
import * as _ from "lodash";
import { makeObservable, observable } from "mobx";
import { lifecycle, strings } from "vscode-lib";
import * as Y from "yjs";
import { FileIdentifier } from "../../identifiers/FileIdentifier";
import { xmlFragmentToMarkdown } from "../../integrations/markdown/export";
import { markdownToYDoc } from "../../integrations/markdown/import";
import ProjectResource from "../ProjectResource";
import { SyncManager } from "./SyncManager";

function isEmptyDoc(doc: Y.Doc) {
  return areDocsEqual(doc, new Y.Doc());
}

// NOTE: only changes in doc xml fragment are checked
function areDocsEqual(doc1: Y.Doc, doc2: Y.Doc) {
  return _.eq(
    (doc1.getXmlFragment("doc").toJSON() as string).replaceAll(
      /block-id=".*"/g,
      ""
    ),
    (doc2.getXmlFragment("doc").toJSON() as string).replaceAll(
      /block-id=".*"/g,
      ""
    )
  );
}

/**
 * Given an identifier, manages local + remote syncing of a Y.Doc
 */
export class YDocFileSyncManager
  extends lifecycle.Disposable
  implements SyncManager
{
  private _ydoc: Y.Doc;
  private watcher: Watcher | undefined;

  public canWrite = true;

  /**
   * Get the managed "doc". Returns:
   * - a Y.Doc encapsulating the loaded doc if available
   * - "not-found" if the document doesn't exist locally / remote
   * - "loading" if we're still loading the document
   *
   * (mobx observable)
   *
   * @type {("loading" | "not-found" | Y.Doc)}
   * @memberof DocConnection
   */
  public doc: "loading" | "not-found" | Y.Doc = "loading";

  public readonly awareness: any = undefined;

  public constructor(public readonly identifier: FileIdentifier) {
    super();
    makeObservable(this, {
      doc: observable.ref,
      canWrite: observable.ref,
    });

    console.log("new docconnection", this.identifier.toString());
    this._ydoc = new Y.Doc({ guid: this.identifier.toString() });
  }

  private async getNewYDocFromDir() {
    const pathWithTrailingSlash = this.identifier.path
      ? strings.trim(this.identifier.path, "/") + "/"
      : "";

    const newDoc = new Y.Doc();
    newDoc.getMap("meta").set("type", "!project");
    const project = new ProjectResource(newDoc, this.identifier);

    this.watcher = this._register(
      new Watcher(pathWithTrailingSlash + "**/*.md")
    );
    this._register(
      this.watcher.onWatchEvent(async (e) => {
        let path = e.path;
        if (pathWithTrailingSlash && !path.startsWith(pathWithTrailingSlash)) {
          throw new Error("file returned with invalid path");
        }
        path = path.substring(pathWithTrailingSlash.length);
        if (e.event === "add") {
          project.files.set(path, {});
        } else if (e.event === "unlink") {
          project.files.delete(path);
        }
      })
    );
    this._ydoc = this.doc = newDoc;
    return newDoc;
  }

  private async getNewYDocFromId() {
    const ret = await readFile(fetch, this.identifier.path);
    if (ret.type === "file") {
      await this.getNewYDocFromFile(ret.contents);
    } else {
      await this.getNewYDocFromDir();
    }
  }

  private async getNewYDocFromFile(contents: string) {
    const newDoc = markdownToYDoc(contents);

    if (isEmptyDoc(this._ydoc)) {
      const update = Y.encodeStateAsUpdate(newDoc);
      Y.applyUpdate(this._ydoc, update);
      this.doc = this._ydoc;
    } else {
      if (!areDocsEqual(this._ydoc, newDoc)) {
        this._ydoc.destroy();
        this._ydoc = this.doc = newDoc;
      }
    }
    this._ydoc.on("update", this.documentUpdateListener);

    this.watcher = this._register(new Watcher(this.identifier.path));

    this._register(
      this.watcher.onWatchEvent(async (event) => {
        if (event.event !== "change") {
          // TODO: support onlink
          return;
        }
        const file = await readFile(fetch, this.identifier.path);

        if (file.type !== "file") {
          throw new Error("unexpected");
        }
        const docData = await this.getNewYDocFromFile(file.contents);
        if (!areDocsEqual(this._ydoc, docData)) {
          this._ydoc.destroy();
          this._ydoc = this.doc = docData;
          this._ydoc.on("update", this.documentUpdateListener);
        }
      })
    );
    return newDoc;
  }

  private getFileFromYDoc(doc: Y.Doc) {
    // const contents = await readFile(this.identifier.path);
    // const nbData = markdownToNotebook(contents);
    if (doc.getMap("meta").get("type") !== "!notebook") {
      throw new Error("invalid type");
    }

    let xml = doc.getXmlFragment("doc");

    return xmlFragmentToMarkdown(xml);
  }

  private documentUpdateListener = async (update: any, origin: any) => {
    if (origin === this) {
      // these are updates that came in from MatrixProvider
      return;
    }
    if (origin?.provider) {
      // update from peer (e.g.: webrtc / websockets). Peer is responsible for sending to Matrix
      return;
    }
    await saveFile(
      fetch,
      this.identifier.path,
      this.getFileFromYDoc(this._ydoc)
    );
  };

  public async initialize() {
    try {
      await this.getNewYDocFromId();
    } catch (e) {
      console.error(e);
      this.doc = "not-found";
    }
  }

  public dispose() {
    super.dispose();
    this._ydoc.off("update", this.documentUpdateListener);
  }

  public on() {}
}
